import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useSetAtom } from "jotai";

import {
  apiErrorAtom,
  apiLoadingAtom,
  cashRegistersAtom,
  createErrorResultFromException,
  IRegisterWithOwner,
} from "@prostocheck/shared";

import { fetchRegisterDetails } from "../../api";
import { notifyError } from "../../notifications";
import { useSession } from "../use-session";
import { IUseCashRegisters } from "./use-cash-registers.models";

/**
 * The list itself came with sign-in; shift state and the last Z-report cost
 * one call per register. Fetches the detail and writes it into
 * `cashRegistersAtom` — nothing else. A component reads `cashRegistersAtom`,
 * `apiLoadingAtom` and `apiErrorAtom` directly for what this call resolved to.
 */
export const useCashRegisters = (): IUseCashRegisters => {
  const { t } = useTranslation();
  const { session } = useSession();
  const setRegisters = useSetAtom(cashRegistersAtom);
  const setIsLoading = useSetAtom(apiLoadingAtom);
  const setErrorResult = useSetAtom(apiErrorAtom);

  const getCashRegisters = useCallback(async () => {
    setIsLoading(true);
    setErrorResult(null);

    try {
      // `useSession` already guarantees a session inside RequireSession's routes.
      const result = await fetchRegisterDetails({
        client: session!.client,
        registers: session!.registers,
      });

      if (!result.success) {
        setErrorResult(result);
        notifyError({ result, t });

        return;
      }

      setRegisters(result.data ?? []);
    } catch (error) {
      const result = createErrorResultFromException<IRegisterWithOwner[]>(error);

      setErrorResult(result);
      notifyError({ result, t });
    } finally {
      setIsLoading(false);
    }
  }, [session, t, setIsLoading, setErrorResult, setRegisters]);

  return { getCashRegisters };
};
